import { nextTick, computed } from 'vue';
import { ModelType } from '@/app/base/schemas/BaseSchema';
import PlanningItemCollection from '@/app/planning/collections/PlanningItemCollection';
import PlanningItem from '@/app/planning/models/PlanningItem';
import User from '@/app/auth/models/User';
import CalendarEventCollection from '@/app/planning/collections/CalendarEventCollection';
import CalendarEvent from '@/app/planning/models/CalendarEvent';
import TaskCollection from '@/app/task/collections/TaskCollection';
import Task from '@/app/task/models/Task';
import WorksitePhaseCollection from '@/app/worksite/collections/WorksitePhaseCollection';
import WorksitePhase from '@/app/worksite/models/WorksitePhase';
import { QueryParameter } from '@/utils/reactiveQueryParameter';
import Worksite from '@/app/worksite/models/Worksite';
import { CalendarEventTypeValue } from '@/app/planning/enums/CalendarEventType';
import moment, { type MomentInstance } from '~~/utils/moment';

export default function useGetPlanningItems(
    filtersItems?: Array<QueryParameter>,
    worksite?: Worksite,
    onCollectionLoaded?: (collection: PlanningItemCollection) => PlanningItemCollection,
    syncToUrl: boolean = true
) {
    const { loading, collection, load, filters } = useCollectionLoader<PlanningItem, PlanningItemCollection>(
        ModelType.PLANNING_ITEMS,
        filtersItems ?? [
            {
                key: 'users',
            },
            {
                key: 'worksites',
            },
            {
                key: 'start',
                defaultValue: moment().startOf('isoWeek').toDateString(),
                transform: (value: string) => moment(value),
            },
            {
                key: 'end',
                defaultValue: moment().endOf('isoWeek').toDateString(),
                transform: (value: string) => moment(value),
            },
            {
                key: 'calendarEventTypes',
                defaultValue: CalendarEventTypeValue.join(','),
            },
        ],
        () => {
            const params = { filter: {} };
            if (filters.getRaw('start') && filters.getRaw('end')) {
                params.filter.dateBetween = `${filters.getRaw('start')},${filters.getRaw('end')}`;
            }

            if (filters.hasItem('worksites') && filters.getRaw('worksites')) {
                params.filter.worksite = {
                    id: filters.getRaw('worksites').split(','),
                };
            }

            if (filters.hasItem('users') && filters.getRaw('users')) {
                params.filter.users = {
                    id: filters.getRaw('users').split(','),
                };
            }

            if (filters.hasItem('calendarEventTypes') && filters.get('calendarEventTypes')) {
                params.filter.calendarEventTypes = filters.get('calendarEventTypes').split(',');
            }

            if (worksite) {
                params.filter.worksite = {
                    id: [worksite.getId()],
                };
            }

            return params;
        },
        (collection: PlanningItemCollection) => {
            // Utiliser un flag pour éviter les appels multiples
            let isUpdating = false;

            const updateSubCollections = () => {
                if (isUpdating) {
                    return;
                }

                isUpdating = true;
                tasks.value = collection.tasks();
                worksitePhases.value = collection.worksitePhases();
                calendarEvents.value = collection.calendarEvents();
                isUpdating = false;
            };

            updateSubCollections();

            collection.setOnUpdateVueCallback(() => {
                updateSubCollections();
            });

            const existingCallback = collection.onSubCollectionChange;
            collection.onSubCollectionChange = () => {
                nextTick(() => {
                    nextTick(() => {
                        updateSubCollections();
                    });
                });
                existingCallback?.();
            };

            if (onCollectionLoaded) {
                collection = onCollectionLoaded(collection);
                const callbackAfterLoad = collection.onSubCollectionChange;

                collection.setOnUpdateVueCallback(() => {
                    updateSubCollections();
                });

                collection.onSubCollectionChange = () => {
                    nextTick(() => {
                        nextTick(() => {
                            updateSubCollections();
                        });
                    });
                    callbackAfterLoad?.();
                };
            }

            return collection;
        },
        syncToUrl
    );

    const tasks = reactiveCollection<Task, TaskCollection>(null);
    const worksitePhases = reactiveCollection<WorksitePhase, WorksitePhaseCollection>(null);
    const calendarEvents = reactiveCollection<CalendarEvent, CalendarEventCollection>(null);
    const days = computed(() => moment.range(filters.get('start'), filters.get('end')).by('days'));

    const getWorksitePhasesForDay = (day: MomentInstance): WorksitePhase[] => {
        return worksitePhases.value?.whereDate(day) ?? [];
    };
    const getTasksForDay = (day: MomentInstance): Task[] => {
        return tasks.value?.whereDate(day) ?? [];
    };

    // Créer un computed qui track calendarEvents.value pour forcer Vue à re-rendre
    // Ce computed sera utilisé dans le template pour que Vue track les changements
    const calendarEventsComputed = computed(() => {
        // Lire calendarEvents.value ET son internalId pour forcer Vue à détecter les changements
        const events = calendarEvents.value;
        return {
            collection: events,
            internalId: events?.getInternalId() ?? null,
            length: events?.length() ?? 0,
        };
    });

    const getCalendarEventsForUserAndDay = (user: User, day: MomentInstance): CalendarEvent[] => {
        // Utiliser le computed pour que Vue track la dépendance
        // Même si on n'utilise que .collection, le computed sera invalidé quand internalId change
        const { collection: events } = calendarEventsComputed.value;
        if (!events) {
            return [];
        }

        const array = events.whereHasUserAndDate(user, day) ?? [];

        return array.sort((a, b) => {
            if (a.startsAt?.isBefore(b.startsAt)) {
                return -1;
            }
            if (a.startsAt?.isAfter(b.startsAt)) {
                return 1;
            }
            return 0;
        });
    };

    onBeforeUnmount(() => {
        tasks.value?.destroy();
        worksitePhases.value?.destroy();
        calendarEvents.value?.destroy();
    });

    return {
        loading,
        collection,
        load,
        filters,
        tasks,
        worksitePhases,
        calendarEvents,
        calendarEventsComputed,
        days,
        getWorksitePhasesForDay,
        getTasksForDay,
        getCalendarEventsForUserAndDay,
    };
}
